import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { insuranceAgentInfoService, InsuranceAgentInfoRec } from '../../../../services/insuranceAgentInfo';
import { vaultService } from '../../../../services/vaultService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('InsuranceAgentInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const InsuranceAgentInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  const [insuranceAgentData, setInsuranceAgentData] = useState<InsuranceAgentInfoRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadInsuranceAgentData();
  }, [targetUserId]);

  const loadInsuranceAgentData = async () => {
    try {
      setLoading(true);
      const data = await insuranceAgentInfoService.getAllInsuranceAgentInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each insurance agent
          const updatedData = await Promise.all(
            data.map(async (agent) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for insurance agent ID:', agent.id);
                const portalData = await vaultService.getPortalCredentials(agent.id, 'insurance-agent');
                logger.debug('[Portal Debug] Portal data retrieved for agent:', agent.id, portalData);
                if (portalData) {
                  agent.username = portalData.username;
                  agent.password = portalData.password;
                  logger.debug('[Portal Debug] Assigned username and password to insurance agent data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for agent:', agent.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for insurance agent:', agent.id, vaultError);
              }
              return agent;
            })
          );

          logger.debug('[Data Debug] Final insurance agent data being set:', updatedData.length, 'agents');
          setInsuranceAgentData(updatedData);
        } else {
          setInsuranceAgentData(data);
        }
      }
    } catch (error) {
      logger.error('Error loading insurance agent data:', error);
      Alert.alert('Error', 'Failed to load insurance agent information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEditInsuranceAgent = (agent: InsuranceAgentInfoRec) => {
    navigation.navigate('InsuranceAgentInfoInput', {
      insuranceAgentId: agent.id,
      isEdit: true
    });
  };

  const handleDeleteInsuranceAgent = async (agent: InsuranceAgentInfoRec) => {
    Alert.alert('Delete', 'Are you sure you want to delete this insurance agent entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await insuranceAgentInfoService.deleteInsuranceAgentInfo(agent.id);

            // Reload data after deletion
            await loadInsuranceAgentData();

            // Check if there are any remaining insurance agent entries
            const remainingData = await insuranceAgentInfoService.getAllInsuranceAgentInfo();
            if (!remainingData || remainingData.length === 0) {
              await tasksService.resetTask('insurance_agent');
              navigation.navigate('FinanceChecklist');
            }

            Alert.alert('Deleted', 'Insurance agent information deleted.');
          } catch (e) {
            logger.error('Delete insurance agent failed:', e);
            Alert.alert('Error', 'Failed to delete insurance agent information');
          }
        }
      },
    ]);
  };

  const handleAddInsuranceAgent = () => {
    navigation.navigate('InsuranceAgentInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Insurance Agent Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading insurance agent information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!insuranceAgentData || insuranceAgentData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Insurance Agent Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added insurance agent information yet."
                : "No insurance agent information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddInsuranceAgent}
              >
                <Text style={styles.addButtonText}>Add Insurance Agent Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Insurance Agent Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {insuranceAgentData.map((agent, index) => (
            <View key={agent.id || index} style={styles.agentCard}>
              <View style={styles.agentHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.agentName}>
                    {agent.agentName || 'Unnamed Agent'}
                  </Text>
                  <Text style={styles.agencyName}>
                    {agent.agencyName || 'No agency specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editAgentButton}
                      onPress={() => handleEditInsuranceAgent(agent)}
                    >
                      <Text style={styles.editAgentButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteAgentButton}
                      onPress={() => handleDeleteInsuranceAgent(agent)}
                    >
                      <Text style={styles.deleteAgentButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Agency Name" value={agent.agencyName || ''} />
              <InfoItem label="Agent Name" value={agent.agentName || ''} />
              <InfoItem label="Insurance Types" value={agent.insuranceType || ''} />
              <InfoItem label="Office Phone" value={formatPhoneNumber(agent.officePhone) || ''} />
              <InfoItem label="Cell Phone" value={formatPhoneNumber(agent.cellPhone) || ''} />
              <InfoItem label="Email" value={agent.email || ''} />
              <InfoItem label="Office Address" value={agent.officeAddress || ''} />
              <InfoItem label="Website" value={agent.website || ''} />
              <InfoItem label="License Number" value={agent.licenseNumber || ''} />
              <InfoItem label="Policy Numbers" value={agent.policyNumbers || ''} />
              <InfoItem label="Coverage Details" value={agent.coverageDetails || ''} />
              
              {agent.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[agent.id] ? agent.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [agent.id]: !prev[agent.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[agent.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {agent.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[agent.id] ? agent.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [agent.id]: !prev[agent.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[agent.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={agent.notes || ''} />
            </View>
          ))}
          
          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherAgentButton}
                onPress={handleAddInsuranceAgent}
              >
                <Text style={styles.addAnotherAgentButtonText}>Add Another Agent</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinanceChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  agentCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  agentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  agentName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  agencyName: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editAgentButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editAgentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteAgentButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteAgentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherAgentButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherAgentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});